<template>
<div class="app-create-new-ai-content is--course">
  <div class="page-main__content grid">
    <div class="app-create-new-ai-content__header col-12">
      <h3 class="app-create-new-ai-content__title">{{ isRegeneratingOutline ? 'Regenerate Outline' : 'Create New Course' }}</h3>
      <a class="app-create-new-ai-content__close element-button--icon element-button" 
        :href="returnLink()">
        <svg class="element-icon--x element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-x"></use></svg>
      </a>
    </div>

    <ul class="app-create-new-ai-content__nav app-vertical-navigation col-12 col-sm-3">
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'basic' ? 'is--selected':'', isCreating ? 'is--static': '']" @click="updateSubscreen('basic')">
        <svg class="element-icon--check-circle element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-check-circle"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Basic details</h4>
      </li>
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'details' ? 'is--selected':'', isCreating ? 'is--static': '']" @click="updateSubscreen('details')">
        <svg class="element-icon--message-text element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-message-text"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Describe your course</h4>
      </li>
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'topics' ? 'is--selected':'', isCreating ? 'is--static': '']" @click="updateSubscreen('topics')">
        <svg class="element-icon--upload-square element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-upload-square"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Select your golden source</h4>
      </li>
      <li :class="['app-vertical-navigation__menu-item', subScreen === 'review' ? 'is--selected':'', isCreating ? 'is--static': '']" @click="updateSubscreen('review')">
        <svg class="element-icon--task-list element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-task-list"></use></svg>
        <h4 class="app-vertical-navigation__menu-item-title">Final details</h4>
      </li>
    </ul>

    <div class="app-create-new-ai-content__info col-12 col-sm-9">
      <publication-ai-app-basic-details
        v-if="subScreen === 'basic'"
        :publication="publication"
        :subScreen="subScreen"
        :editorMaxCharacterLimit_default="editorMaxCharacterLimit_default"
        :disabledButton="disabled"
        :hasErrors="hasErrors"
        :errorMessage="errorMessage"
        :errors="errors"
        :saveTrigger="saveTrigger"
        ref="basicDetails"
        @submissionOfData="handleSubmission($event)">
      </publication-ai-app-basic-details>

      <publication-ai-app-course-description
        v-if="subScreen === 'details' && !isCreating"
        :publication="publication"
        :subScreen="subScreen"
        :editorMaxCharacterLimit_default="editorMaxCharacterLimit_default"
        :editorMaxCharacterLimit_extra_prompt="editorMaxCharacterLimit_extra_prompt"
        :disabledButton="disabled"
        :hasErrors="hasErrors"
        :errorMessage="errorMessage"
        :errors="errors"
        :saveTrigger="saveTrigger"
        ref="courseDetails"
        @previousScreen="!hasErrors ? setSubscreen('basic') : null"
        @submissionOfData="handleSubmission($event)">
      </publication-ai-app-course-description>

      <project-sources-selection
        v-if="!isCreating && subScreen === 'topics'"
        :topics="topics"
        :selectedTopics="selectedTopics"
        :aiSuggestions="aiSuggestions"
        :saveTrigger="saveTrigger"
        :hasErrors="hasErrors"
        :errorMessage="errorMessage"
        :errors="errors"
        @getAISuggestions="getAISuggestions($event)"
        @updateSelectedTopics="onTopicSelection($event)"
        @previousScreen="!hasErrors ? setSubscreen('details'): null"
        @saveData="handleSubmission($event)">
      </project-sources-selection>

      <publication-ai-app-final-review-upload-sources
        v-if="subScreen === 'review'"
        :publication="publication"
        :topics="topics"
        :saveTrigger="saveTrigger"
        :disabledButton="disabled"
        :selectedTopics="selectedTopics"
        :isRegenerating="isRegeneratingOutline"
        @previousScreen="!hasErrors ? setSubscreen('topics'): null"
        @submit="submit()">
      </publication-ai-app-final-review-upload-sources>

    </div>
  </div>
</div>
</template>

<script>
import _ from 'lodash'
import api from '../api/ai'
import publisher from '../api/publisher'

export default {
  components: { },
  props: {
    project: {
      type: Object,
    },
    projectSource: {
      type: Object,
      default: null
    },
    item: {
      type: Object,
      default: null
    },
    topics: {
      type: Array,
      default: null
    },
  },
  data() {
    return {
      publicationData: this.item ?? null,
      disabled: false,
      subScreen: 'basic',
      errorMessage: null,
      descriptionEntries: null,
      saveTrigger: false,
      aiSuggestions: [],
      errors: [],
      editorMaxCharacterLimit_default:1000,
      editorMaxCharacterLimit_extra_prompt:2000,
      isRegeneratingOutline: false
    }
  },
  computed: {
    publication:{
      get() {
        return this.publicationData !== 'null' && this.publicationData !== null ? this.publicationData : {
          name: '',
          description: '',
        }
      },
      set(val) {
        this.publicationData = val
      }
    },
    isCreating () {
      return !this.publication?.id
    },
    hasErrors () {
      return !!(this.errorMessage || (this.errors?.message ?? this.errors?.errors?.message))
    },
    selectedTopics () {
      return this.publication.ai_preferences?.input_topics || []
    }
  },
  methods: {
    getAISuggestions() {
      console.log('Getting AI suggestions')
      this.errors = []
      this.errorMessage = null
      this.disabled = true
      api.getAISuggestions(this.publication.id)
        .then(result => {
          if (result.errors) {
            console.log('Errors getting AI suggestions:', result)
            this.errors = result.errors
            this.errorMessage = result.message
          } else {
            console.log('AI suggested topics:', result.topics)
            this.publication.ai_preferences.input_topics = result.topics
            this.aiSuggestions = result.topics
          }
          this.disabled = false
        })
        .catch(error => {
          console.error('Error fetching AI suggestions:', error)
          this.errorMessage = 'An error occurred while fetching AI suggestions.'
          this.disabled = false
        })
    },
    setSubscreen(type) {
      this.subScreen = type
    },
    updateSubscreen(type) {
      if (this.isCreating || this.hasErrors)
        return

      this.saveTrigger = true
      setTimeout(() => {
        if(!this.isCreating) {
          this.subScreen = type
        } else {
          this.subScreen = 'basic'
        }
      }, 100);
    },
    returnLink(event) {
      return this.isRegeneratingOutline ? `/publications/${this.publication.id}/course-outline` : `/projects/${this.project.id}/courses`
    },
    onTopicSelection(event) {
      this.publication.ai_preferences.input_topics = event
    },
    async save(nextScreen = 'details', menuSelection = null, params = {}) {
      console.log('saving...', nextScreen, this.publication, params)
      this.saveTrigger = false

      this.disabled = true

      if (this.isCreating) {

        params['ai'] = true

        const result = await publisher.create(this.project.id, params)

        console.log(result)
        if (result.errors) {
          this.disabled = false
          this.errors = result.errors
          this.errorMessage = result.message
          return
        }

        window.location.href = `/publications/${result.id}/course-sources#details`;
        return
      }

      if (this.subScreen === 'topics') {
        params = { input_topics: this.selectedTopics }
      }

      // api call to prepare outline
      const result = await publisher.update(this.publication.id, params)

      console.log(result)
      if (result.errors) {
        // catch errors
        this.errors = result.errors
        this.errorMessage = result.message
      } else {
        this.errors = []
        this.errorMessage = null
        this.publication = result
        if (menuSelection !== 'menuSwap') {
          this.setSubscreen(nextScreen)
        }
      }
      this.disabled = false
    },
    async submit() {
      this.disabled = true

      let params = this.descriptionEntries

      params.dismissAi = true // Force to only use source files

      // api call to prepare outline
      const result = await api.generateOutline(this.publication.id, params)
      this.saveTrigger = false
      console.log(result)
      if (result.errors) {
        // catch errors
        this.disabled = false
        this.errors = result.errors
        this.errorMessage = result.message
      } else {
        // redirect
        window.location.href = '/projects/' + this.project.id + '/courses';
      }
    },
    handleSubmission(event) {
      if (event.params) {
        this.save(event.subScreen, event.menuSwap, event.params)
      } else {
        this.save(event.subScreen, event.menuSwap)
      }
      if(event.entries) {
        this.descriptionEntries = event.entries
      }
    }
  },
  watch: {
    topics: {
      handler(newTopics) {
        const allCurrentUUIDs = newTopics.flatMap(domain => domain.topics.map(topic => topic.uuid));
        if (this.publication?.ai_preferences?.input_topics) {
          const filtered = this.publication.ai_preferences.input_topics.filter(uuid =>
            allCurrentUUIDs.includes(uuid)
          );
          if (filtered.length !== this.publication.ai_preferences.input_topics.length) {
            this.publication.ai_preferences.input_topics = filtered
          }
        }
      },
      deep: true,
    }
  },
  mounted() {
    if (window.location.hash && window.location.hash.toLowerCase() === '#details')
      this.subScreen = 'details'

    this.descriptionEntries = this.publication?.ai_preferences
    
    const outlineStatus = this.publication?.ai_preferences?.outlineStatus
    this.isRegeneratingOutline = !!outlineStatus;
  },
  created() {
    if (this.topics) {
      const allCurrentUUIDs = this.topics.flatMap(domain => domain.topics.map(topic => topic.uuid));
      if (this.publication?.ai_preferences?.input_topics) {
        const filtered = this.publication.ai_preferences.input_topics.filter(uuid =>
          allCurrentUUIDs.includes(uuid)
        );
        if (filtered.length !== this.publication.ai_preferences.input_topics.length) {
          this.publication.ai_preferences.input_topics = filtered
        }
      }
    }
  }
}
</script>